import { useState } from "react"
import Manage from "@/v2-backoffice-client/components/manage"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import type { UserTenantFragment } from "@/v2-backoffice-client/user/tenant/__types__/user.tenant.fragment"
import { useUserTenantTypeQuery } from "@/v2-backoffice-client/user/tenant/type/user.tenant.type.query"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import TenantUserManageMemberItem from "@/v2-backoffice-client/tenant/user/manage/TenantUserManageMemberItem"
import TenantUserManageAddedItem from "@/v2-backoffice-client/tenant/user/manage/TenantUserManageAddedItem"
import TenantUserManageSearch from "@/v2-backoffice-client/tenant/user/manage/TenantUserManageSearch"

type AddedItem = {
  user: UserFragment
  roleCode: number
}

export type TenantUserManageDialogProps = {
  open: boolean
  onOpenChange(open: boolean): void
  tenantId: number
  tenantName?: string
  existingUserTenants?: UserTenantFragment[]
}

function TenantUserManageDialog(props: TenantUserManageDialogProps) {
  const {
    open,
    onOpenChange,
    tenantId,
    tenantName,
    existingUserTenants = [],
  } = props

  const [search, setSearch] = useState("")
  const [searchAll, setSearchAll] = useState(true)

  const [addedItems, setAddedItems] = useState<Map<number, AddedItem>>(
    new Map(),
  )
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set())

  const { data: typeData } = useUserTenantTypeQuery({
    fetchPolicy: "cache-first",
  })

  const typesByCode = new Map(
    (typeData?.getUserTenantTypes ?? []).map((t) => [t.Code, t.Name]),
  )

  function handleOpenChange(nextOpen: boolean) {
    onOpenChange(nextOpen)
    if (!nextOpen) {
      setAddedItems(new Map())
      setRemovedIds(new Set())
      setSearch("")
      setSearchAll(true)
    }
  }

  function handleAdded(user: UserFragment, roleCode: number) {
    setAddedItems((prev) => {
      const next = new Map(prev)
      next.set(user.Id, { user, roleCode })
      return next
    })
    setRemovedIds((prev) => {
      if (!prev.has(user.Id)) return prev
      const next = new Set(prev)
      next.delete(user.Id)
      return next
    })
  }

  function handleRemoved(userId: number) {
    setRemovedIds((prev) => new Set(prev).add(userId))
    setAddedItems((prev) => {
      if (!prev.has(userId)) return prev
      const next = new Map(prev)
      next.delete(userId)
      return next
    })
  }

  const existingUserIds = new Set(existingUserTenants.map((ut) => ut.UserId))

  function isMember(userId: number) {
    if (removedIds.has(userId)) return false
    if (addedItems.has(userId)) return true
    return existingUserIds.has(userId)
  }

  const searchLower = search.toLowerCase()

  function matchesName(name: string, extra: string) {
    if (!searchLower) return true
    return (
      name.toLowerCase().includes(searchLower) ||
      extra.toLowerCase().includes(searchLower)
    )
  }

  const visibleExisting = existingUserTenants.filter((ut) => {
    if (removedIds.has(ut.UserId)) return false
    if (!searchAll) return true
    const user = ut.User
    return matchesName(
      formatUserName(user?.FirstName, user?.LastName),
      user?.Email ?? "",
    )
  })

  const visibleAdded = [...addedItems.values()].filter((item) => {
    if (existingUserIds.has(item.user.Id)) return false
    if (!searchAll) return true
    return matchesName(
      formatUserName(item.user.FirstName, item.user.LastName),
      item.user.Email ?? "",
    )
  })

  const totalMembers = visibleExisting.length + visibleAdded.length
  const title = tenantName
    ? `Manage Users for Team: ${tenantName}`
    : "Manage Users for Team"

  return (
    <Manage.Dialog open={open} onOpenChange={handleOpenChange} title={title}>
      <Manage.Search
        onSearch={setSearch}
        placeholder="Search users by email..."
        searchAll={searchAll}
        onSearchAllChange={setSearchAll}
      />

      {totalMembers > 0 && (
        <Manage.Section label={`Users already in the team (${totalMembers})`}>
          <Manage.List>
            {visibleExisting.map((ut) => (
              <TenantUserManageMemberItem
                key={ut.Id}
                userTenant={ut}
                tenantId={tenantId}
                onRemoved={() => handleRemoved(ut.UserId)}
              />
            ))}
            {visibleAdded.map((item) => (
              <TenantUserManageAddedItem
                key={item.user.Id}
                user={item.user}
                tenantId={tenantId}
                roleName={typesByCode.get(item.roleCode)}
                onRemoved={() => handleRemoved(item.user.Id)}
              />
            ))}
          </Manage.List>
        </Manage.Section>
      )}

      <TenantUserManageSearch
        open={open}
        tenantId={tenantId}
        debouncedSearch={search}
        isMember={isMember}
        onAdded={handleAdded}
      />
    </Manage.Dialog>
  )
}

export default TenantUserManageDialog
